/**
 * Sent notification emails for a license.
 */

import { client } from "@/hera-api/client/client.gen";

export type NotificationEmailKind =
  | "upcoming_renewal"
  | "needs_attention"
  | "compliance_issue_flagged"
  | "comment";

export interface LicenseNotificationEmailListItem {
  id: string;
  kind: NotificationEmailKind;
  subject: string;
  to_email: string;
  sent_at: string;
}

export interface LicenseNotificationEmailListResponse {
  items: LicenseNotificationEmailListItem[];
}

export interface LicenseNotificationEmailDetail {
  id: string;
  kind: NotificationEmailKind;
  subject: string;
  to_email: string;
  bcc: string | null;
  sent_at: string;
  html_body: string;
  text_body: string;
}

export async function listLicenseNotificationEmails(
  licenseId: string,
): Promise<LicenseNotificationEmailListResponse> {
  const result = await client.get<LicenseNotificationEmailListResponse>({
    url: `/shared/licenses/${licenseId}/notification-emails`,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to load notification emails",
    );
  }
  return result.data as LicenseNotificationEmailListResponse;
}

export async function getLicenseNotificationEmail(
  licenseId: string,
  emailId: string,
): Promise<LicenseNotificationEmailDetail> {
  const result = await client.get<LicenseNotificationEmailDetail>({
    url: `/shared/licenses/${licenseId}/notification-emails/${emailId}`,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to load notification email",
    );
  }
  return result.data as LicenseNotificationEmailDetail;
}

export function notificationEmailKindLabel(
  kind: NotificationEmailKind,
): string {
  if (kind === "upcoming_renewal") return "Upcoming renewal";
  if (kind === "compliance_issue_flagged") return "Compliance issue";
  if (kind === "comment") return "Comment";
  return "Needs attention";
}
